import { Button, Grid, Group } from '@mantine/core'
import { useForm, yupResolver } from '@mantine/form'
import { useRouter } from 'next/router'
import { FormEvent, useState } from 'react'
import { useSWRConfig } from 'swr'

import {
  api,
  errorHandler,
  getUrlString,
  isValidTaxDocument,
  notificationHandler,
  Yup
} from '@/lib/utils'
import { CPF_CNPJ_VALIDATION_REGEX } from '@/lib/utils/taxDocument'
import { BankAccount } from '@/types'

import * as BankAccountFields from './Fields'

interface Props {
  onSelect?: (bankAccount: BankAccount) => void
  handleCloseDrawer?: () => void
  bankAccount?: BankAccount | null
}

interface FormValues {
  transferMethod: 'pix' | 'ted'
  holder: string
  taxDocument: string
  pixKey: string
  bank: string
  agencyNumber: string
  agencyCheckNumber: string
  accountNumber: string
  accountCheckNumber: string
  accountType: string
}

export default function Order({ onSelect, handleCloseDrawer, bankAccount = null }: Props) {
  // Hooks
  const router = useRouter()
  const { mutate: mutateGlobal } = useSWRConfig()

  // Constants
  const { site } = router.query || {}
  const siteSlug = getUrlString(site)
  const isEditing = Boolean(bankAccount?.uid)

  // States
  const [bank, setBank] = useState<any>(bankAccount?.bank || null)
  const [submitting, setSubmitting] = useState<boolean>(false)

  // Mantine Form
  const initialValues: FormValues = {
    transferMethod: bankAccount?.transferMethod || 'pix',
    holder: bankAccount?.holder || '',
    taxDocument: bankAccount?.taxDocument || '',
    pixKey: bankAccount?.pixKey || '',
    bank: bankAccount?.bank?.code || '',
    agencyNumber: bankAccount?.agencyNumber?.toString() || '',
    agencyCheckNumber: bankAccount?.agencyCheckNumber?.toString() || '',
    accountNumber: bankAccount?.accountNumber?.toString() || '',
    accountCheckNumber: bankAccount?.accountCheckNumber?.toString() || '',
    accountType: bankAccount?.accountType || 'checking'
  }

  const schema = Yup.object().shape({
    transferMethod: Yup.string()
      .oneOf(['pix', 'ted'])
      .required('Selecione a forma de transferência'),
    holder: Yup.string()
      .matches(
        /^([a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð ])+$/u,
        'Digite apenas letras'
      )
      .required('Você precisa informar o nome conforme está cadastrado em sua conta no banco')
      .min(3)
      .max(65),
    taxDocument: Yup.string()
      .matches(CPF_CNPJ_VALIDATION_REGEX, 'CPF/CNPJ inválido')
      .test('valid-tax-document', 'CPF/CNPJ inválido', value => !value || isValidTaxDocument(value))
      .required('Insira um CPF ou CNPJ válido'),
    pixKey: Yup.string().when('transferMethod', {
      is: 'pix',
      then: schema => schema.required('Você precisa informar a sua chave Pix'),
      otherwise: schema => schema.notRequired()
    }),
    bank: Yup.string().when('transferMethod', {
      is: 'ted',
      then: schema => schema.required('É preciso selecionar qual o banco em que você possui conta'),
      otherwise: schema => schema.notRequired()
    }),
    agencyNumber: Yup.string().when('transferMethod', {
      is: 'ted',
      then: schema => schema.required('Você precisa informar a agência de sua conta no banco'),
      otherwise: schema => schema.notRequired()
    }),
    agencyCheckNumber: Yup.string(),
    accountNumber: Yup.string().when('transferMethod', {
      is: 'ted',
      then: schema => schema.required('Precisamos do número de sua conta no banco'),
      otherwise: schema => schema.notRequired()
    }),
    accountCheckNumber: Yup.string(),
    accountType: Yup.string().when('transferMethod', {
      is: 'ted',
      then: schema => schema.required('Selecione o tipo da sua conta no banco'),
      otherwise: schema => schema.notRequired()
    })
  })

  const form = useForm({
    validate: yupResolver(schema),
    validateInputOnChange: true,
    validateInputOnBlur: true,
    initialValues
  })

  // Actions
  const handleSubmit = async () => {
    setSubmitting(true)

    try {
      const { transferMethod, holder, taxDocument, pixKey, ...rest } = form.values
      const data = {
        transferMethod,
        holder,
        taxDocument,
        ...(transferMethod === 'pix' ? { pixKey } : rest)
      }

      const request = isEditing
        ? api.patch(`/${siteSlug}/bank-accounts/${bankAccount?.uid}/`, data)
        : api.post(`/${siteSlug}/bank-accounts/`, data)

      await request.then((res: any) => {
        notificationHandler({
          variant: 'success',
          message: isEditing
            ? 'Dados bancários atualizados com sucesso'
            : 'Sua nova conta bancária foi criada com sucesso'
        })

        if (res?.data) {
          form.reset()
          setBank(null)
        }

        onSelect?.(res?.data)
        handleCloseDrawer?.()

        setSubmitting(false)
        mutateGlobal(`/${siteSlug}/bank-accounts/`)
      })
    } catch (error: any) {
      setSubmitting(false)
      const erros = errorHandler(error) || {}
      notificationHandler({
        variant: 'error',
        message:
          erros?.messages?.[0] ||
          (isEditing
            ? 'Houve um erro durante a atualização da conta bancária'
            : 'Houve um erro durante o cadastro da conta bancária')
      })
    }
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Grid gutter={2}>
        <Grid.Col xs={12}>
          <BankAccountFields.TransferMethodField
            inputProps={{
              value: form.values.transferMethod,
              onChange: (value: 'pix' | 'ted') => form.setFieldValue('transferMethod', value)
            }}
          />
        </Grid.Col>
        <Grid.Col xs={12}>
          <BankAccountFields.HolderField
            inputProps={{
              ...form.getInputProps('holder'),
              onBlur: ({ currentTarget: { value } }: FormEvent<HTMLInputElement>) => {
                form.setFieldValue('holder', value.replace(/[^\S\r\n]+/g, ' ').trim())
              }
            }}
          />
        </Grid.Col>
        <Grid.Col xs={12}>
          <BankAccountFields.TaxDocumentField
            inputProps={{ ...form.getInputProps('taxDocument') }}
          />
        </Grid.Col>
        <Grid.Col xs={12}>
          {form.values.transferMethod === 'pix' ? (
            <BankAccountFields.PixKeyField inputProps={{ ...form.getInputProps('pixKey') }} />
          ) : (
            <BankAccountFields.CommonBankSearchField
              inputProps={{
                selected: bank,
                defaultValue: '',
                defaultSearchValue: '',
                onSelect: (choice: any) => {
                  setBank(choice)
                  form.setTouched({ bank: true })
                  form.setFieldValue('bank', choice.code)
                },
                ...form.getInputProps('bank')
              }}
            />
          )}
        </Grid.Col>
        {form.values.transferMethod === 'ted' && (
          <>
            <Grid.Col xs={12}>
              <BankAccountFields.AccountTypeField
                inputProps={{ ...form.getInputProps('accountType') }}
              />
            </Grid.Col>
            <Grid.Col xs={12}>
              <Grid gutter="sm">
                <Grid.Col xs={8}>
                  <BankAccountFields.AgencyNumberField
                    inputProps={{ ...form.getInputProps('agencyNumber') }}
                  />
                </Grid.Col>
                <Grid.Col xs={4}>
                  <BankAccountFields.AgencyCheckNumberField
                    inputProps={{
                      value: form.values.agencyCheckNumber,
                      onChange: (event: any) =>
                        form.setFieldValue('agencyCheckNumber', event.target.value)
                    }}
                  />
                </Grid.Col>
              </Grid>
            </Grid.Col>
            <Grid.Col xs={12}>
              <Grid gutter="sm">
                <Grid.Col xs={8}>
                  <BankAccountFields.AccountNumberField
                    inputProps={{ ...form.getInputProps('accountNumber') }}
                  />
                </Grid.Col>
                <Grid.Col xs={4}>
                  <BankAccountFields.AccountCheckNumberField
                    inputProps={{
                      value: form.values.accountCheckNumber,
                      onChange: (event: any) =>
                        form.setFieldValue('accountCheckNumber', event.target.value)
                    }}
                  />
                </Grid.Col>
              </Grid>
            </Grid.Col>
          </>
        )}
        <Grid.Col xs={12}>
          <Group position="right" mt="sm">
            <Button
              type="submit"
              loading={submitting}
              disabled={!form.isValid() || !form.isDirty()}>
              {isEditing ? 'Salvar alterações' : 'Salvar'}
            </Button>
          </Group>
        </Grid.Col>
      </Grid>
    </form>
  )
}
